import React, { useEffect, useState, createRef } from 'react'
import {
  Checkbox,
  createStyles,
  Fade,
  FormControlLabel,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import Button from '../Button/Button'
import Commitment from '../Commitment/Commitment'
import Loader from '../Loader/Loader'
import TextField from '../TextField/TextField'
import clsx from 'clsx'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import TextBox from '../TextField/TextBox'
import ReCAPTCHA from 'react-google-recaptcha'
import Link from '../Link/Link'
import CMSText, { isPrismicText, prismicText } from '../Global/CMSText'
import {
  isLoginSSO,
  isSSO,
  professionalAccountOnly,
} from '../../utils/constants'
import { PrismicAccountData } from '../../interfaces/common/prismicAccount'
import { PrismicGeneralData } from '../../interfaces/common/prismicGeneralConfig'
import {
  isB2BRegistrationEnabled,
  enableCustomerAccountId,
} from '@/utils/constants'

const recaptchaKey = process.env.RECAPTCHA_SITE_KEY

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    registerFormCard: {
      background: theme.palette.common.white,
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      [theme.breakpoints.up('sm')]: {
        width: 600,
        margin: 'auto',
      },
    },
    formFooter: {
      textAlign: 'center',
    },
    link: {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    commitment: {
      maxWidth: 400,
      margin: 'auto',
    },
    marginBottom: {
      marginBottom: theme.spacing(2),
    },
    errorForm: {
      color: 'red',
    },
    hide: {
      opacity: 0,
      transition: theme.transitions.create('opacity'),
    },
    show: {
      opacity: 1,
      transition: theme.transitions.create('opacity'),
    },
    label: {
      display: 'block',
      cursor: 'pointer',
      fontSize: 9,
      paddingLeft: theme.spacing(1.25),
      marginBottom: theme.spacing(0.5),
    },
    checkbox: {
      '& .paragraph-class': {
        marginTop: 0,
        '&:last-child': {
          marginBottom: 0,
        },
      },
    },
    recaptcha: {
      display: 'flex',
      marginBottom: theme.spacing(2),
      justifyContent: 'center',
    },
    customCgl: {
      marginLeft: theme.spacing(4),
    },
    takeBackTitle: {
      textAlign: 'center',
    },
    selectContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
    selectLabel: {
      display: 'block',
      cursor: 'pointer',
      textAlign: 'inherit',
      fontSize: 11,
      paddingLeft: theme.spacing(1.25),
      marginBottom: theme.spacing(0.5),
    },
    select: {
      display: 'block',
      padding: theme.spacing(1, 0.5),
      fontFamily: theme.typography.fontFamily,
      width: '100%',
      fontSize: 14,
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.extraColors.greyLight}`,
      color: theme.typography.body1.color,
      backgroundColor: theme.extraColors.greyLight,
    },
  })
)

type Tprops = {
  switchForm: any
  submitRegistration: any
  text: PrismicAccountData
  hasError: boolean
  errorPhone: boolean
  t?: any
  errorEmailUsed?: boolean
  isLoading?: boolean
  isFunnel: boolean
  isTakeBack?: boolean
  takeBackTitle?: any
  generalConfigData?: PrismicGeneralData
}

const RegisterForm = ({
  switchForm,
  submitRegistration,
  text,
  t,
  errorPhone,
  hasError,
  errorEmailUsed,
  isLoading,
  isFunnel,
  isTakeBack = false,
  takeBackTitle,
  generalConfigData,
}: Tprops) => {
  const [isB2BAccount, setIsB2BAccount] = useState(professionalAccountOnly)
  const classes = useStyles()
  const [firstName, setFirstName] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [validPassword, setValidPassword] = useState('')

  // B2B fields
  const [siret, setSiret] = useState('')
  const [shopName, setShopName] = useState('')

  // Brand customer fields
  const [isBrandCustomer, setIsBrandCustomer] = useState(false)
  const [customerAccountId, setCustomerAccountId] = useState('')

  // @todo: combine error logic and preferably use Formik or react-forms for form management
  const [errorPassword, setErrorPassword] = useState(false)
  const [errorEmail, setErrorEmail] = useState(false)
  const [strongPasswordError, setStrongPasswordError] = useState(false)
  const [errorSiret, setErrorSiret] = useState(false)

  const [newsletter, setNewsLetter] = useState(false)
  const [cgu, setCGU] = useState(false)
  const { trackEvent } = useMatomo()
  const commitmentOnRegister = text?.is_displayed_on_register
  const bottomText = text?.register_bottom_text
  const hasCGU = isPrismicText(text?.cgu)
  const [captchaOK, setCaptchaOK] = useState(!recaptchaKey)
  const recaptchaRef = createRef()
  const [registeredStoreSlug, setRegisteredStoreSlug] = useState('')
  const [genderValue, setGenderValue] = useState('m')

  const handleGenderValue = (event) => {
    setGenderValue(event.target.value)
  }

  useEffect(() => {
    typeof localStorage !== 'undefined' &&
      setRegisteredStoreSlug(
        JSON.parse(localStorage.getItem('selectedStore'))?.slug
      )
  }, [])

  const setEmailWithVerification = (event) => {
    setEmail(event)
    function containsWhitespace(str) {
      return /\s/.test(str)
    }
    if (containsWhitespace(event)) {
      setErrorEmail(true)
    } else {
      setErrorEmail(false)
    }
  }

  const changeNewsletter = () => {
    setNewsLetter(!newsletter)
  }

  const changeCGU = () => {
    setCGU(!cgu)
  }

  const onReCAPTCHAChange = (captchaCode) => {
    if (!captchaCode) {
      return
    } else {
      setCaptchaOK(true)
    }
  }

  const checkPassword = (event) => {
    setPassword(event)
    const regexStrongPassword = new RegExp(
      '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{10,})'
    )
    if (regexStrongPassword.test(event)) {
      setStrongPasswordError(false)
    } else {
      setStrongPasswordError(true)
    }
  }

  const submitForm = (e) => {
    e.preventDefault()
    if (isB2BAccount && (siret === '' || shopName === '')) {
      return
    }
    if (
      password !== '' &&
      firstName !== '' &&
      name !== '' &&
      email !== '' &&
      captchaOK
    ) {
      if (password === validPassword) {
        const data = {
          gender: text.disable_gender ? 'u' : genderValue.trim(),
          firstName: firstName.trim(),
          lastName: name.trim(),
          email: email.trim(),
          phoneNumber: phone.trim(),
          plainPassword: password,
          subscribedToNewsletter: newsletter,
          ...(isB2BRegistrationEnabled &&
            isB2BAccount && {
              siret: siret.trim(),
              shopName: shopName.trim(),
              isB2b: true,
            }),
          ...(enableCustomerAccountId &&
            isB2BRegistrationEnabled &&
            isB2BAccount &&
            isBrandCustomer && {
              isBrandCustomer: isBrandCustomer,
              customerAccountId: customerAccountId.trim(),
            }),
        }
        trackEvent({
          category: 'User Information',
          action: `User submit registration`,
        })
        submitRegistration(data)
        setErrorPassword(false)
      } else {
        setErrorPassword(true)
      }
    }
  }

  useEffect(() => {
    // If cgu are not filled in prismic, force cgu to true in order to create account
    if (!hasCGU) {
      setCGU(true)
    }
  }, [cgu])

  const triggerEvent = () => {
    trackEvent({
      category: 'User Information',
      action: `User is typing in email field on registration form`,
    })
  }

  const setSiretWithValidation = (value) => {
    setSiret(value)
    // SIRET must be exactly 14 digits
    const siretRegex = /^\d{14}$/
    if (value && !siretRegex.test(value.trim())) {
      setErrorSiret(true)
    } else {
      setErrorSiret(false)
    }
  }

  return (
    <Fade in>
      <form
        className={
          !isFunnel
            ? `account-card register-card ${classes.registerFormCard}`
            : `account-form register-form ${classes.root}`
        }
      >
        <Typography
          variant={'h3'}
          className={clsx('account__title', classes.marginBottom)}
          align="center"
        >
          {prismicText(text?.title, t('texts:account:title'))}
        </Typography>
        <Typography
          variant={'body1'}
          className={clsx({ account__desc: isTakeBack })}
        >
          <b>
            {!isTakeBack
              ? prismicText(text?.login_title)
              : prismicText(takeBackTitle, t('texts:takeBack:register_text'))}
          </b>
        </Typography>
        <Typography
          variant={'body2'}
          className={clsx('account__desc', classes.marginBottom)}
        >
          <CMSText data={text?.text} defaultText={t('texts:account:text')} />
        </Typography>
        {isB2BRegistrationEnabled && !professionalAccountOnly && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {t('texts:account:account_type') || 'Type de compte'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!isB2BAccount}
                    onChange={() => setIsB2BAccount && setIsB2BAccount(false)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('texts:account:personal_account') ||
                      'Compte particulier'}
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isB2BAccount}
                    onChange={() => setIsB2BAccount && setIsB2BAccount(true)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('texts:account:b2b_account') ||
                      'Compte professionnel (B2B)'}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        )}
        {(!isSSO || (isSSO && isLoginSSO === 'false')) && (
          <>
            <Grid
              container
              spacing={1}
              className={clsx(
                'account__fields',
                'account__hidden',
                classes.marginBottom
              )}
            >
              {!text.disable_gender && (
                <Grid item sm={2} xs={4} className={classes.selectContainer}>
                  <label className={clsx(classes.selectLabel, 'label')}>
                    <CMSText
                      asText
                      data={text?.gender_label}
                      defaultText={t('texts:account:gender_label')}
                    />
                  </label>
                  <select
                    value={genderValue}
                    onChange={handleGenderValue}
                    required={true}
                    name="title"
                    className={clsx(classes.select, 'select')}
                  >
                    <option value={'m'}>
                      {prismicText(
                        text?.gender_male,
                        t('texts:account:gender_male')
                      )}
                    </option>
                    <option value={'f'}>
                      {prismicText(
                        text?.gender_female,
                        t('texts:account:gender_female')
                      )}
                    </option>
                  </select>
                </Grid>
              )}

              <Grid item sm={text.disable_gender ? 6 : 5} xs={12}>
                <TextField
                  label={prismicText(
                    text?.firstname,
                    t('texts:account:firstname')
                  )}
                  type="text"
                  name="firstName"
                  placeholder={prismicText(
                    text?.firstname,
                    t('texts:account:firstname')
                  )}
                  value={firstName}
                  required={true}
                  onChange={setFirstName}
                />
              </Grid>
              <Grid item sm={text.disable_gender ? 6 : 5} xs={12}>
                <TextField
                  label={prismicText(
                    text?.lastname,
                    t('texts:account:lastname')
                  )}
                  type="text"
                  name="lastName"
                  placeholder={prismicText(
                    text?.lastname,
                    t('texts:account:lastname')
                  )}
                  value={name}
                  required={true}
                  onChange={setName}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  label={prismicText(text?.email, t('texts:account:email'))}
                  type="text"
                  name="email"
                  placeholder={prismicText(
                    text?.email,
                    t('texts:account:email')
                  )}
                  value={email}
                  required={true}
                  onChange={setEmailWithVerification}
                  onClick={triggerEvent}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                {
                  <TextField
                    label={prismicText(text?.phone, t('texts:account:phone'))}
                    type="text"
                    name="phone"
                    placeholder={prismicText(
                      text?.phone,
                      t('texts:account:phone')
                    )}
                    value={phone}
                    required={true}
                    onChange={setPhone}
                    phoneInputInfosIcon={{
                      tooltipTitle: generalConfigData?.phone_input_infos,
                    }}
                  />
                }
              </Grid>
              {isB2BRegistrationEnabled && isB2BAccount && (
                <>
                  <Grid item sm={6} xs={12}>
                    <TextField
                      label={t('texts:account:siret') || 'SIRET'}
                      type="text"
                      name="siret"
                      placeholder={t('texts:account:siret') || 'SIRET'}
                      value={siret}
                      required={true}
                      onChange={setSiretWithValidation}
                      className={errorSiret && classes.errorForm}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <TextField
                      label={
                        t('texts:account:company_name') || 'Nom de la société'
                      }
                      type="text"
                      name="shopName"
                      placeholder={
                        t('texts:account:company_name') || 'Nom de la société'
                      }
                      value={shopName}
                      required={true}
                      onChange={setShopName}
                    />
                  </Grid>
                </>
              )}
              {enableCustomerAccountId &&
                isB2BRegistrationEnabled &&
                isB2BAccount && (
                  <>
                    <Grid item xs={12}>
                      <FormControlLabel
                        className={classes.checkbox}
                        control={
                          <Checkbox
                            checked={isBrandCustomer}
                            onChange={() =>
                              setIsBrandCustomer(!isBrandCustomer)
                            }
                            name="isBrandCustomer"
                          />
                        }
                        label={
                          <Typography variant={'body2'}>
                            {prismicText(
                              text?.is_customer,
                              t('texts:account:is_customer')
                            )}
                          </Typography>
                        }
                      />
                    </Grid>
                    {isBrandCustomer && (
                      <Grid item xs={12}>
                        <TextField
                          label={prismicText(
                            text?.customer_account_id,
                            t('texts:account:customer_account_id')
                          )}
                          type="text"
                          name="customerAccountId"
                          placeholder={t('texts:account:customer_account_id')}
                          value={customerAccountId}
                          onChange={setCustomerAccountId}
                        />
                      </Grid>
                    )}
                  </>
                )}
              <Grid item sm={6} xs={12}>
                <TextField
                  label={prismicText(
                    text?.password,
                    t('texts:account:password')
                  )}
                  type="password"
                  name="password"
                  placeholder={prismicText(
                    text?.password,
                    t('texts:account:password')
                  )}
                  value={password}
                  required={true}
                  className={errorPassword && classes.errorForm}
                  onChange={checkPassword}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField
                  label={prismicText(
                    text?.repeat_password,
                    t('texts:account:repeat_password')
                  )}
                  type="password"
                  name="validPassword"
                  className={errorPassword && classes.errorForm}
                  placeholder={prismicText(
                    text?.repeat_password,
                    t('texts:account:repeat_password')
                  )}
                  value={validPassword}
                  required={true}
                  onChange={setValidPassword}
                />
              </Grid>
              {isPrismicText(text?.newsletter) && (
                <Grid item xs={12}>
                  <FormControlLabel
                    className={classes.checkbox}
                    control={
                      <Checkbox
                        checked={newsletter}
                        onChange={changeNewsletter}
                        name="checkedA"
                      />
                    }
                    label={
                      <Typography variant={'body2'} component={'div'}>
                        <CMSText
                          data={text?.newsletter}
                          defaultText={t('texts:account:newsletter')}
                        />
                      </Typography>
                    }
                  />
                </Grid>
              )}
              {hasCGU && (
                <Grid item xs={12}>
                  <FormControlLabel
                    className={classes.checkbox}
                    control={
                      <Checkbox
                        checked={cgu}
                        onChange={changeCGU}
                        name="cgu_agree"
                        data-testid={'cgu_agree'}
                      />
                    }
                    label={
                      <Typography variant={'body2'} component={'div'}>
                        <CMSText data={text?.cgu} />
                      </Typography>
                    }
                  />
                  {registeredStoreSlug && (
                    <Typography
                      variant={'body2'}
                      component={'span'}
                      className={classes.customCgl}
                    >
                      <Link href={`/pages/cgl-${registeredStoreSlug}`}>
                        <a>
                          <CMSText
                            data={text?.custom_cgu_link}
                            asText
                            defaultText={t(
                              'texts:funnel:payment:custom_cgu_link'
                            )}
                          />
                        </a>
                      </Link>
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>

            <div className={clsx(classes.formFooter, classes.marginBottom)}>
              {errorPassword && (
                <Typography
                  variant={'body2'}
                  color={'error'}
                  className={classes.marginBottom}
                >
                  {prismicText(
                    text?.password_error,
                    t('texts:account:password_error')
                  )}
                </Typography>
              )}
              {strongPasswordError && (
                <Typography
                  variant={'body2'}
                  color={'error'}
                  className={classes.marginBottom}
                >
                  {prismicText(
                    text?.password_strength_error,
                    t('texts:account:password_strength_error')
                  )}
                </Typography>
              )}
              {hasError && (
                <Typography
                  variant={'body2'}
                  color={'error'}
                  className={classes.marginBottom}
                >
                  {prismicText(
                    text?.error_happend,
                    t('texts:account:error_happend')
                  )}
                </Typography>
              )}
              {errorEmailUsed && (
                <Typography
                  variant={'body2'}
                  color={'error'}
                  className={classes.marginBottom}
                >
                  {prismicText(text?.email_error, t('texts:account:mail_used'))}
                </Typography>
              )}
              {errorEmail && (
                <Typography
                  variant={'body2'}
                  color={'error'}
                  className={classes.marginBottom}
                >
                  {prismicText(
                    text?.email_format_incorrect,
                    t('texts:account:email_format_incorrect')
                  )}
                </Typography>
              )}
              {errorPhone && (
                <Typography
                  variant={'body2'}
                  color={'error'}
                  align={'center'}
                  className={classes.marginBottom}
                >
                  {prismicText(
                    text?.error_phone,
                    t('texts:account:error_phone')
                  )}
                </Typography>
              )}
              {errorSiret && (
                <Typography
                  variant={'body2'}
                  color={'error'}
                  align={'center'}
                  className={classes.marginBottom}
                >
                  {t('texts:account:error_siret') ||
                    'Le SIRET doit contenir exactement 14 chiffres'}
                </Typography>
              )}
              {isPrismicText(bottomText) && (
                <Typography
                  variant={'body2'}
                  component={'div'}
                  className={'account__bottom-text'}
                >
                  <CMSText data={bottomText} />
                </Typography>
              )}
              {recaptchaKey && (
                <div className={classes.recaptcha}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaKey}
                    onChange={onReCAPTCHAChange}
                  />
                </div>
              )}
              <Button
                variant={'contained'}
                color={'secondary'}
                type={'submit'}
                data-testid={'registrationButton'}
                className={'register--validation__btn'}
                onClick={submitForm}
                disabled={
                  firstName === '' ||
                  name === '' ||
                  email === '' ||
                  phone === '' ||
                  phone === undefined ||
                  password === '' ||
                  validPassword === '' ||
                  !cgu ||
                  !captchaOK ||
                  strongPasswordError ||
                  (isB2BRegistrationEnabled &&
                    isB2BAccount &&
                    (siret === '' || shopName === '' || errorSiret)) ||
                  (enableCustomerAccountId &&
                    isB2BAccount &&
                    isB2BRegistrationEnabled &&
                    isBrandCustomer &&
                    customerAccountId === '')
                }
              >
                <span className={isLoading ? classes.hide : classes.show}>
                  {prismicText(
                    text?.register_button,
                    t('texts:account:register_button')
                  )}
                </span>
                {isLoading && <Loader />}
              </Button>
            </div>
            <div className={clsx(classes.formFooter, classes.marginBottom)}>
              <Typography
                variant={'body2'}
                className={clsx('account__switch', classes.link)}
              >
                <span onClick={() => switchForm(4)} data-testid={'toLogin'}>
                  <CMSText
                    asText
                    data={text?.login_link}
                    defaultText={t('texts:account:login_link')}
                  />
                </span>
              </Typography>
            </div>
          </>
        )}
        {commitmentOnRegister && !isTakeBack ? (
          <div className={clsx(classes.commitment, 'commitment__area')}>
            <Commitment
              icon={
                text?.protected_data_image?.url
                  ? text?.protected_data_image?.url
                  : null
              }
              title={prismicText(
                text?.title_data,
                t('texts:account:title_data')
              )}
              desc={
                <CMSText
                  data={text?.text_data}
                  defaultText={t('texts:account:text_data')}
                />
              }
              center={false}
            />
          </div>
        ) : (
          <Typography variant={'caption'} component={'div'}>
            <CMSText data={text?.alternative_text_for_register} />
          </Typography>
        )}
        {text?.is_display_text_box && <TextBox text={text?.data_text} />}
      </form>
    </Fade>
  )
}

export default RegisterForm
